// Auto-generated by @fermata/grpc-zustand - DO NOT EDIT
// Generated from: @proto/gen/users/v1/service

import type {
  BumpAuthzVersionRequest,
  CreateTenantRequest,
  DeleteTenantRequest,
  GetTenantRequest,
  ListTenantsRequest,
  UpdateTenantRequest,
} from "@proto/gen/users/v1/service"
import { useEffect, useMemo } from "react"
import { requestKey } from "../../runtime/middleware"
import { useTenantServiceStore } from "./store"

/**
 * React hook for TenantService.listTenants
 *
 * @param req - Request parameters (optional for auto-fetch)
 * @param autoFetch - Whether to automatically call the method on mount/change (default: true)
 * @returns Method state (data, loading, error) and refetch function
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { data, loading, error, refetch } = useListTenants(request, true)
 *
 *   if (loading) return <div>Loading...</div>
 *   if (error) return <div>Error: {error.message}</div>
 *
 *   return <div>{JSON.stringify(data)}</div>
 * }
 * ```
 */
export function useListTenants(req?: ListTenantsRequest, autoFetch = true) {
  // Content-based key so inline request objects don't loop, and so each distinct
  // request reads/writes its own cache entry instead of a shared per-method slot.
  const reqKey = useMemo(() => requestKey(req), [req])
  const entry = useTenantServiceStore((s) => s.listTenants.byKey?.[reqKey])
  const listTenantsAsync = useTenantServiceStore((s) => s.listTenantsAsync)

  // biome-ignore lint/correctness/useExhaustiveDependencies: req tracked via reqKey; store fns are stable
  useEffect(() => {
    if (autoFetch && req) {
      void listTenantsAsync(req)
    }
  }, [autoFetch, reqKey])

  return {
    data: entry?.data,
    loading: entry?.loading ?? false,
    error: entry?.error,
    refetch: () => req && listTenantsAsync(req),
  }
}

/**
 * React hook for TenantService.createTenant
 *
 * @param req - Request parameters (optional for auto-fetch)
 * @param autoFetch - Whether to automatically call the method on mount/change (default: true)
 * @returns Method state (data, loading, error) and refetch function
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { data, loading, error, refetch } = useCreateTenant(request, true)
 *
 *   if (loading) return <div>Loading...</div>
 *   if (error) return <div>Error: {error.message}</div>
 *
 *   return <div>{JSON.stringify(data)}</div>
 * }
 * ```
 */
export function useCreateTenant(req?: CreateTenantRequest, autoFetch = true) {
  // Content-based key so inline request objects don't loop, and so each distinct
  // request reads/writes its own cache entry instead of a shared per-method slot.
  const reqKey = useMemo(() => requestKey(req), [req])
  const entry = useTenantServiceStore((s) => s.createTenant.byKey?.[reqKey])
  const createTenantAsync = useTenantServiceStore((s) => s.createTenantAsync)

  // biome-ignore lint/correctness/useExhaustiveDependencies: req tracked via reqKey; store fns are stable
  useEffect(() => {
    if (autoFetch && req) {
      void createTenantAsync(req)
    }
  }, [autoFetch, reqKey])

  return {
    data: entry?.data,
    loading: entry?.loading ?? false,
    error: entry?.error,
    refetch: () => req && createTenantAsync(req),
  }
}

/**
 * React hook for TenantService.getTenant
 *
 * @param req - Request parameters (optional for auto-fetch)
 * @param autoFetch - Whether to automatically call the method on mount/change (default: true)
 * @returns Method state (data, loading, error) and refetch function
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { data, loading, error, refetch } = useGetTenant(request, true)
 *
 *   if (loading) return <div>Loading...</div>
 *   if (error) return <div>Error: {error.message}</div>
 *
 *   return <div>{JSON.stringify(data)}</div>
 * }
 * ```
 */
export function useGetTenant(req?: GetTenantRequest, autoFetch = true) {
  // Content-based key so inline request objects don't loop, and so each distinct
  // request reads/writes its own cache entry instead of a shared per-method slot.
  const reqKey = useMemo(() => requestKey(req), [req])
  const entry = useTenantServiceStore((s) => s.getTenant.byKey?.[reqKey])
  const getTenantAsync = useTenantServiceStore((s) => s.getTenantAsync)

  // biome-ignore lint/correctness/useExhaustiveDependencies: req tracked via reqKey; store fns are stable
  useEffect(() => {
    if (autoFetch && req) {
      void getTenantAsync(req)
    }
  }, [autoFetch, reqKey])

  return {
    data: entry?.data,
    loading: entry?.loading ?? false,
    error: entry?.error,
    refetch: () => req && getTenantAsync(req),
  }
}

/**
 * React hook for TenantService.updateTenant
 *
 * @param req - Request parameters (optional for auto-fetch)
 * @param autoFetch - Whether to automatically call the method on mount/change (default: true)
 * @returns Method state (data, loading, error) and refetch function
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { data, loading, error, refetch } = useUpdateTenant(request, true)
 *
 *   if (loading) return <div>Loading...</div>
 *   if (error) return <div>Error: {error.message}</div>
 *
 *   return <div>{JSON.stringify(data)}</div>
 * }
 * ```
 */
export function useUpdateTenant(req?: UpdateTenantRequest, autoFetch = true) {
  // Content-based key so inline request objects don't loop, and so each distinct
  // request reads/writes its own cache entry instead of a shared per-method slot.
  const reqKey = useMemo(() => requestKey(req), [req])
  const entry = useTenantServiceStore((s) => s.updateTenant.byKey?.[reqKey])
  const updateTenantAsync = useTenantServiceStore((s) => s.updateTenantAsync)

  // biome-ignore lint/correctness/useExhaustiveDependencies: req tracked via reqKey; store fns are stable
  useEffect(() => {
    if (autoFetch && req) {
      void updateTenantAsync(req)
    }
  }, [autoFetch, reqKey])

  return {
    data: entry?.data,
    loading: entry?.loading ?? false,
    error: entry?.error,
    refetch: () => req && updateTenantAsync(req),
  }
}

/**
 * React hook for TenantService.deleteTenant
 *
 * @param req - Request parameters (optional for auto-fetch)
 * @param autoFetch - Whether to automatically call the method on mount/change (default: true)
 * @returns Method state (data, loading, error) and refetch function
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { data, loading, error, refetch } = useDeleteTenant(request, true)
 *
 *   if (loading) return <div>Loading...</div>
 *   if (error) return <div>Error: {error.message}</div>
 *
 *   return <div>{JSON.stringify(data)}</div>
 * }
 * ```
 */
export function useDeleteTenant(req?: DeleteTenantRequest, autoFetch = true) {
  // Content-based key so inline request objects don't loop, and so each distinct
  // request reads/writes its own cache entry instead of a shared per-method slot.
  const reqKey = useMemo(() => requestKey(req), [req])
  const entry = useTenantServiceStore((s) => s.deleteTenant.byKey?.[reqKey])
  const deleteTenantAsync = useTenantServiceStore((s) => s.deleteTenantAsync)

  // biome-ignore lint/correctness/useExhaustiveDependencies: req tracked via reqKey; store fns are stable
  useEffect(() => {
    if (autoFetch && req) {
      void deleteTenantAsync(req)
    }
  }, [autoFetch, reqKey])

  return {
    data: entry?.data,
    loading: entry?.loading ?? false,
    error: entry?.error,
    refetch: () => req && deleteTenantAsync(req),
  }
}

/**
 * React hook for TenantService.bumpAuthzVersion
 *
 * @param req - Request parameters (optional for auto-fetch)
 * @param autoFetch - Whether to automatically call the method on mount/change (default: true)
 * @returns Method state (data, loading, error) and refetch function
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { data, loading, error, refetch } = useBumpAuthzVersion(request, true)
 *
 *   if (loading) return <div>Loading...</div>
 *   if (error) return <div>Error: {error.message}</div>
 *
 *   return <div>{JSON.stringify(data)}</div>
 * }
 * ```
 */
export function useBumpAuthzVersion(req?: BumpAuthzVersionRequest, autoFetch = true) {
  // Content-based key so inline request objects don't loop, and so each distinct
  // request reads/writes its own cache entry instead of a shared per-method slot.
  const reqKey = useMemo(() => requestKey(req), [req])
  const entry = useTenantServiceStore((s) => s.bumpAuthzVersion.byKey?.[reqKey])
  const bumpAuthzVersionAsync = useTenantServiceStore((s) => s.bumpAuthzVersionAsync)

  // biome-ignore lint/correctness/useExhaustiveDependencies: req tracked via reqKey; store fns are stable
  useEffect(() => {
    if (autoFetch && req) {
      void bumpAuthzVersionAsync(req)
    }
  }, [autoFetch, reqKey])

  return {
    data: entry?.data,
    loading: entry?.loading ?? false,
    error: entry?.error,
    refetch: () => req && bumpAuthzVersionAsync(req),
  }
}
